import React, { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { SpinLoader } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { StyledDashboardTab, StyledDashboardTabsPanel } from "@/components/pages/Dashboard/styled";
import { InvoiceCategory } from "@/shared/domain/invoices";
import { useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import useInvoicesQueries from "../useInvoicesQueries";
import InvoiceTabPanel from "./components/InvoiceTabPanel";
import { TabIndex } from "./constants";

const InvoicePanel: FC = () => {
    const { t } = useTranslation();
    const { navToInvoices } = useNavigation();
    const [selectedTab, setSelectedTab] = useState(TabIndex.UNPAID);

    const { tableMode, useInvoicesPageQuery } = useInvoicesQueries();

    const {
        isFetched: isFetchedPaid,
        isFetching: isFetchingPaid,
        result: invoicesPaid,
        orderBy: orderByPaid,
        handleSort: handleSortPaid,
    } = useInvoicesPageQuery({ defaultCategory: InvoiceCategory.PAID });

    const {
        isFetching: isFetchingUnpaid,
        isFetched: isFetchedUnpaid,
        result: invoicesUnpaid,
        orderBy: orderByUnpaid,
        handleSort: handleSortUnpaid,
    } = useInvoicesPageQuery({ defaultCategory: InvoiceCategory.NOT_PAID });

    const {
        isFetching: isFetchingOthers,
        isFetched: isFetchedOthers,
        result: invoicesOthers,
        orderBy: orderByOthers,
        handleSort: handleSortOthers,
    } = useInvoicesPageQuery({ defaultCategory: InvoiceCategory.OTHERS });

    const displayTabs = [invoicesUnpaid, invoicesPaid, invoicesOthers].filter((array) => array.length > 0).length > 1;
    const allPanelsEmpty =
        [invoicesUnpaid, invoicesPaid, invoicesOthers].filter((array) => array.length === 0).length === 3;

    const selectedTabToCategory = (index: TabIndex): InvoiceCategory => {
        switch (index) {
            case TabIndex.UNPAID:
                return InvoiceCategory.NOT_PAID;

            case TabIndex.PAID:
                return InvoiceCategory.PAID;

            case TabIndex.OTHERS:
                return InvoiceCategory.OTHERS;
        }
    };

    const handleSeeMoreInvoices = () => {
        return navToInvoices(selectedTabToCategory(selectedTab));
    };

    useEffect(() => {
        if (isFetchingPaid && isFetchingUnpaid && isFetchingOthers && displayTabs) {
            tagManager.pushViewItemListEvent("invoices", "Invoices", true);
        }
        const tabs = [
            {
                tabIndex: TabIndex.UNPAID,
                size: invoicesUnpaid.length,
            },
            {
                tabIndex: TabIndex.PAID,
                size: invoicesPaid.length,
            },
            {
                tabIndex: TabIndex.OTHERS,
                size: invoicesOthers.length,
            },
        ];
        const filteredTabs = tabs.filter((tab) => tab.size > 0);
        if (filteredTabs.length > 0) {
            setSelectedTab(filteredTabs[0].tabIndex);
        }
    }, [invoicesUnpaid, invoicesPaid, invoicesOthers]);

    if (
        isFetchingPaid ||
        isFetchingUnpaid ||
        isFetchingOthers ||
        !isFetchedPaid ||
        !isFetchedUnpaid ||
        !isFetchedOthers
    ) {
        return <SpinLoader />;
    }
    return (
        <div>
            {displayTabs && (
                <Tabs value={selectedTab} onChange={(_event, tab) => setSelectedTab(tab)}>
                    {invoicesUnpaid.length > 0 && (
                        <StyledDashboardTab
                            label={t("Invoices.ToPay")}
                            data-testid="invoices_tabs-tab-not_paid"
                            value={TabIndex.UNPAID}
                        />
                    )}
                    {invoicesPaid.length > 0 && (
                        <StyledDashboardTab
                            label={t("Invoices.Paid")}
                            data-testid="invoices_tabs-tab-paid"
                            value={TabIndex.PAID}
                        />
                    )}
                    {invoicesOthers.length > 0 && (
                        <StyledDashboardTab
                            label={t("Invoices.Others")}
                            data-testid="invoices_tabs-tab-other"
                            value={TabIndex.OTHERS}
                        />
                    )}
                </Tabs>
            )}
            <StyledDashboardTabsPanel $withoutTabs={!displayTabs}>
                {(invoicesUnpaid.length > 0 || allPanelsEmpty) && (
                    <InvoiceTabPanel
                        tableMode={tableMode}
                        panelIndex={TabIndex.UNPAID}
                        dataTestId="invoices_tabs-tab_panel-not_paid"
                        panelValue={displayTabs ? selectedTab : TabIndex.UNPAID}
                        invoices={invoicesUnpaid}
                        orderBy={orderByUnpaid}
                        handleSort={handleSortUnpaid}
                        handleSeeMore={handleSeeMoreInvoices}
                    />
                )}
                {invoicesPaid.length > 0 && (
                    <InvoiceTabPanel
                        tableMode={tableMode}
                        panelIndex={TabIndex.PAID}
                        dataTestId="invoices_tabs-tab_panel-paid"
                        panelValue={displayTabs ? selectedTab : TabIndex.PAID}
                        invoices={invoicesPaid}
                        orderBy={orderByPaid}
                        handleSort={handleSortPaid}
                        handleSeeMore={handleSeeMoreInvoices}
                    />
                )}
                {invoicesOthers.length > 0 && (
                    <InvoiceTabPanel
                        tableMode={tableMode}
                        panelIndex={TabIndex.OTHERS}
                        dataTestId="invoices_tabs-tab_panel-other"
                        panelValue={displayTabs ? selectedTab : TabIndex.OTHERS}
                        invoices={invoicesOthers}
                        orderBy={orderByOthers}
                        handleSort={handleSortOthers}
                        handleSeeMore={handleSeeMoreInvoices}
                    />
                )}
            </StyledDashboardTabsPanel>
        </div>
    );
};

export default InvoicePanel;
